import React, { createContext, useState, useCallback, useEffect } from 'react';

export const PageContext = createContext();

// Map page keys to URL slugs (only pages that need a slug)
const PAGE_SLUG_MAP = {
  'manifesto': '/manifesto',
  'news': '/news',
  'intelligence': '/intelligence',
  'services': '/services',
  'ai-transformation': '/transformation',
  'ai-training': '/training',
  'admin': '/admin',
  'apply': '/apply'
};
const SLUG_PAGE_MAP = Object.fromEntries(
  Object.entries(PAGE_SLUG_MAP).map(([page, slug]) => [slug, page])
);

const HIDDEN_PAGES = new Set(['companies', 'company-detail']);

const getVisiblePage = (page) => HIDDEN_PAGES.has(page) ? 'landing' : page;

const getPageInfoFromURL = () => {
  const path = window.location.pathname;
  if (path.startsWith('/campaign/')) {
    const parts = path.split('/');
    return { page: getVisiblePage('campaign'), companyId: null, serviceId: null, campaignSlug: parts[2] || null };
  }
  if (path === '/services' || path.startsWith('/services/')) {
    return { page: getVisiblePage('services'), companyId: null, serviceId: null, campaignSlug: null };
  }
  return { page: getVisiblePage(SLUG_PAGE_MAP[path] || 'landing'), companyId: null, serviceId: null, campaignSlug: null };
};

export const PageProvider = ({ children }) => {
  const initialPageInfo = getPageInfoFromURL();
  const [currentPage, setCurrentPage] = useState(initialPageInfo.page);
  const [currentCompanyId, setCurrentCompanyId] = useState(initialPageInfo.companyId);
  const [currentServiceId, setCurrentServiceId] = useState(initialPageInfo.serviceId);
  const [currentCampaignSlug, setCurrentCampaignSlug] = useState(initialPageInfo.campaignSlug);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [newsSearchQuery, setNewsSearchQuery] = useState('');

  const navigateTo = useCallback((page) => {
    const visiblePage = getVisiblePage(page);
    setCurrentPage(visiblePage);
    setCurrentCompanyId(null);
    setCurrentServiceId(null);
    setCurrentCampaignSlug(null);
    setIsMenuOpen(false);
    window.scrollTo(0, 0);

    // Update browser URL
    const slug = PAGE_SLUG_MAP[visiblePage] || '/';
    window.history.pushState({ page: visiblePage, companyId: null }, '', slug);
  }, []);

  const navigateToCompany = useCallback(() => {
    setCurrentPage('landing');
    setCurrentCompanyId(null);
    setCurrentServiceId(null);
    setCurrentCampaignSlug(null);
    setIsMenuOpen(false);
    window.scrollTo(0, 0);

    window.history.pushState({ page: 'landing', companyId: null }, '', '/');
  }, []);

  const navigateToService = useCallback((serviceId = null) => {
    setCurrentPage('services');
    setCurrentCompanyId(null);
    setCurrentServiceId(serviceId);
    setCurrentCampaignSlug(null);
    setIsMenuOpen(false);
    window.scrollTo(0, 0);

    const slug = serviceId ? `/services/${serviceId}` : '/services';
    window.history.pushState({ page: 'services', companyId: null, serviceId }, '', slug);
  }, []);

  // Handle browser back/forward
  useEffect(() => {
    const handlePopState = (event) => {
      if (event.state && event.state.page) {
        const visiblePage = getVisiblePage(event.state.page);
        setCurrentPage(visiblePage);
        setCurrentCompanyId(visiblePage === 'landing' ? null : event.state.companyId || null);
        setCurrentServiceId(event.state.serviceId || null);
        setCurrentCampaignSlug(event.state.campaignSlug || null);
      } else {
        const info = getPageInfoFromURL();
        setCurrentPage(info.page);
        setCurrentCompanyId(info.companyId);
        setCurrentServiceId(info.serviceId);
        setCurrentCampaignSlug(info.campaignSlug || null);
      }
      setIsMenuOpen(false);
      window.scrollTo(0, 0);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const toggleMenu = useCallback(() => {
    setIsMenuOpen(prev => !prev);
  }, []);

  const value = {
    currentPage,
    currentCompanyId,
    currentServiceId,
    currentCampaignSlug,
    isMenuOpen,
    navigateTo,
    navigateToCompany,
    navigateToService,
    newsSearchQuery,
    setNewsSearchQuery,
    toggleMenu
  };

  return (
    <PageContext.Provider value={value}>
      {children}
    </PageContext.Provider>
  );
};

export const usePageContext = () => {
  const context = React.useContext(PageContext);
  if (!context) {
    throw new Error('usePageContext must be used within PageProvider');
  }
  return context;
};
